import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import type { SiteSettings } from '../../lib/supabase';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadSettings();
    checkUser();
    
    // Gerçek zamanlı dinleme - Değişiklikler anında görünür!
    const channel = supabase
      .channel('site_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings'
        },
        () => {
          console.log('✅ Ayarlar güncellendi, yeniden yükleniyor...');
          loadSettings();
        }
      )
      .subscribe();

    // Auth değişikliklerini dinle
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkUserRole(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });
    
    return () => {
      supabase.removeChannel(channel);
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    if (user) {
      await checkUserRole(user.id);
    }
  };

  const checkUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Rol kontrolü hatası:', error);
      }
      
      if (data) {
        setIsAdmin(data.role === 'admin');
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Rol kontrolü hatası:', error);
      setIsAdmin(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('setting_key', 'site_settings')
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Ayarlar yüklenirken hata:', error);
        return;
      }
      
      if (data) {
        console.log('✅ Ayarlar yüklendi:', data);
        setSettings(data);
      }
    } catch (error) {
      console.error('Ayarlar yüklenemedi:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    window.location.href = '/';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Ana Sayfa' },
    { path: '/services', label: 'Hizmetler' },
    { path: '/about', label: 'Hakkımızda' },
    { path: '/contact', label: 'İletişim' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A0A0A]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            {settings?.logo_url ? (
              <div className="w-12 h-12 flex items-center justify-center">
                <img 
                  src={settings.logo_url} 
                  alt={settings.site_name || 'Logo'} 
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D] rounded-lg">
                <i className="ri-car-washing-line text-2xl text-black"></i>
              </div>
            )}
            <span className="text-2xl font-bold text-white whitespace-nowrap">
              {settings?.site_name || 'MRT Soft'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`whitespace-nowrap text-base font-medium transition-colors cursor-pointer ${
                  isActive(link.path)
                    ? 'text-[#FCD34D]'
                    : 'text-gray-300 hover:text-[#FCD34D]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Sosyal Medya İkonları */}
            <div className="flex items-center space-x-3">
              {settings?.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-lg hover:scale-110 transition-transform cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-fill text-lg text-white"></i>
                </a>
              )}
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-[#1877F2] rounded-lg hover:scale-110 transition-transform cursor-pointer"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-lg text-white"></i>
                </a>
              )}
            </div>
            
            <Link
              to="/appointment"
              className="whitespace-nowrap px-6 py-2.5 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer"
            >
              Randevu Al
            </Link>

            {/* Giriş/Çıkış Butonları */}
            {user ? (
              <div className="flex items-center space-x-3">
                {isAdmin && (
                  <Link
                    to="/admin/dashboard"
                    className="whitespace-nowrap px-4 py-2 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition-all cursor-pointer"
                  >
                    Panel
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="whitespace-nowrap px-4 py-2 bg-red-500/80 text-white font-medium rounded-lg hover:bg-red-500 transition-all cursor-pointer"
                >
                  Çıkış Yap
                </button>
              </div>
            ) : (
              <Link
                to="/auth/login"
                className="whitespace-nowrap px-6 py-2.5 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all cursor-pointer"
              >
                Giriş Yap
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white text-2xl cursor-pointer"
          >
            <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-[#0A0A0A]/95 backdrop-blur-md">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`whitespace-nowrap text-base font-medium transition-colors cursor-pointer ${
                    isActive(link.path)
                      ? 'text-[#FCD34D]'
                      : 'text-gray-300 hover:text-[#FCD34D]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobil Sosyal Medya */}
              {(settings?.instagram_url || settings?.facebook_url) && (
                <div className="flex items-center space-x-3 pt-2">
                  {settings?.instagram_url && (
                    <a
                      href={settings.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-lg hover:scale-110 transition-transform cursor-pointer"
                      aria-label="Instagram"
                    >
                      <i className="ri-instagram-fill text-lg text-white"></i>
                    </a>
                  )}
                  {settings?.facebook_url && (
                    <a
                      href={settings.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center bg-[#1877F2] rounded-lg hover:scale-110 transition-transform cursor-pointer"
                      aria-label="Facebook"
                    >
                      <i className="ri-facebook-fill text-lg text-white"></i>
                    </a>
                  )}
                </div>
              )}
              
              <Link
                to="/appointment"
                onClick={() => setIsMobileMenuOpen(false)}
                className="whitespace-nowrap px-6 py-2.5 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer text-center"
              >
                Randevu Al
              </Link>

              {/* Mobil Giriş/Çıkış */}
              {user ? (
                <>
                  {isAdmin && (
                    <Link
                      to="/admin/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="whitespace-nowrap px-6 py-2.5 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all cursor-pointer text-center"
                    >
                      Panel
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="whitespace-nowrap px-6 py-2.5 bg-red-500/80 text-white font-semibold rounded-lg hover:bg-red-500 transition-all cursor-pointer text-center"
                  >
                    Çıkış Yap
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="whitespace-nowrap px-6 py-2.5 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all cursor-pointer text-center"
                >
                  Giriş Yap
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
